import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Form,
  ListGroup,
  InputGroup,
  Button,
} from "react-bootstrap";
import { VolumeUpFill } from "react-bootstrap-icons";
import {
  fetchWords,
  setSearchTerm,
  clearResults,
  setTriggerTranslate,
} from "../redux/slices/searchSlice";
import AddToUserVocabulary from "./AddToUserVocabulary";
import handlePronunciation from "../utils/handlePronunciation";

export default function Search() {
  const dispatch = useDispatch(); // Initialize the Redux dispatch function
  const { searchTerm, results, loading, isSearchByWord } = useSelector(
    (state) => state.search
  ); // Access the necessary state from the Redux store

  // Effect to handle fetching words when the search term changes
  useEffect(() => {
    if (searchTerm) {
      const timer = setTimeout(() => {
        dispatch(fetchWords({ term: searchTerm, triggerTranslate: false }));
      }, 300); // 300ms delay to ensure the user finishes typing before fetching

      return () => clearTimeout(timer); // Clear the timeout if the user types again before the delay ends
    } else {
      dispatch(clearResults()); // Clear results if the search term is empty
    }
  }, [dispatch, searchTerm]); // Run the effect whenever the searchTerm changes

  // Handle the search input field's change event
  function handleSearch(event) {
    dispatch(setSearchTerm(event.target.value)); // Update the search term in the Redux store
  }

  // Handle the keydown event for the search input field
  function handleKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent the default Enter key behavior
      if (results.length === 0) {
        dispatch(setTriggerTranslate(true)); // Trigger translation if no results
        dispatch(fetchWords({ term: searchTerm, triggerTranslate: true })); // Fetch words with translation enabled
      }
    }
  }

  return (
    <Container className="d-flex flex-column align-items-center my-2">
      <Form className="w-100">
        <InputGroup>
          {/* Search input field */}
          <Form.Control
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
            aria-label="Search"
            style={{ height: 42 }}
          />
          {/* Button to trigger pronunciation of the search term */}
          <Button
            variant="outline-secondary"
            onClick={() => handlePronunciation(searchTerm)}
            style={{ marginLeft: "5px" }}
          >
            <VolumeUpFill width="24" height="24" />
          </Button>
        </InputGroup>
      </Form>

      {/* Display a loading message if words are being fetched */}
      {loading ? (
        <p className="mt-3">Searching...</p>
      ) : (
        // Display the list of search results
        <ListGroup className="w-100 mt-3" style={{ width: "280px" }}>
          {results.map((item, index) => (
            <ListGroup.Item
              key={`${item.word}-${index}`}
              className="d-flex justify-content-between align-items-center"
            >
              {/* Display the translation if searched by word, or the word itself if searched by translation */}
              <span>{isSearchByWord ? item.translation : item.word}</span>
              {/* Component to add the word and its translation to the user's vocabulary */}
              <AddToUserVocabulary
                word={item.word}
                translation={item.translation}
              />
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </Container>
  );
}
